import {
  SALES_STATUS_LABELS,
  SALES_STATUS_ORDER,
  type SalesAction,
  type SalesStatus,
} from '@shared/types/sales-action';

/// restore 判定に必要な最小フィールドだけを受ける (テスト容易性のため)。
/// `restore_target` は `unknown` で受ける — サーバ値を信頼せず防御的に parse する境界
/// (`SalesAction.restore_target: SalesStatus | null` という型契約はここでは前提にしない)。
type RestoreView = Pick<SalesAction, 'action_status' | 'sales_status'> & {
  restore_target: unknown;
};

function parseSalesStatus(value: unknown): SalesStatus | null {
  return typeof value === 'string' &&
    (SALES_STATUS_ORDER as string[]).includes(value)
    ? (value as SalesStatus)
    : null;
}

function isResultStatus(status: SalesStatus): boolean {
  return status === 'won' || status === 'lost';
}

/**
 * 結果行 (completed + won/lost) の戻し先ステータス。 backend (`SalesActionDto.restore_target`)
 * が `sales_status_events` から導出した値をそのまま使う (A10 移行 / CU-86eydemk1
 * フォローアップ — 旧実装はレガシー JSONB 追記列を自前で読んでいたが、その列への新規
 * append は停止済みで新規結果化行が推定不能になっていた。 当該列は migration 0020 で
 * DROP 済み)。
 *
 * ここでの再検証は防御的なもの (`action_status` / `sales_status` 自体の整合、
 * `restore_target` が結果ステータスでないこと) で、backend の SQL 実装
 * (`sales_actions/row.rs` の `restore_target` 導出) と同じ意味論を保つ。 実際の適用可否は
 * サーバ側 (`validate_restore_status`) が最終判定する。
 */
export function inferRestoreTarget(action: RestoreView): SalesStatus | null {
  if (action.action_status !== 'completed') return null;
  if (!isResultStatus(action.sales_status)) return null;
  const restoreTo = parseSalesStatus(action.restore_target);
  if (restoreTo === null || isResultStatus(restoreTo)) return null;
  return restoreTo;
}

/**
 * 「ステータスを戻す」確認ダイアログの本文。 契約が紐づく行では、 契約は
 * 自動変更されないことを明示する (契約タブでの修正は別作業)。
 */
export function buildRestoreConfirmMessage(
  action: Pick<SalesAction, 'linked_contract_id'>,
  restoreTo: SalesStatus,
): string {
  const lines = [
    'この結果を現在の次アクションに戻しますか？',
    '',
    `ステータスは「${SALES_STATUS_LABELS[restoreTo]}」に戻ります。`,
  ];
  if (action.linked_contract_id !== null) {
    lines.push(
      '紐づいている契約は変更されません。必要なら契約タブで修正してください。',
    );
  }
  return lines.join('\n');
}
